import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAxios } from '../../utils/hook/useAxios';

const WEEKDAYS = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];

const ALL_MONTHS = {
  0: 'Janvier',
  1: 'Février',
  2: 'Mars',
  3: 'Avril',
  4: 'Mai',
  5: 'Juin',
  6: 'Juillet',
  7: 'Août',
  8: 'Septembre',
  9: 'Octobre',
  10: 'Novembre',
  11: 'Décembre',
};

// Affichage d'une journée du calendrier
function CalendarDayButton({ date, price, unavailable }) {
  const state = !unavailable ? '' : price ? ' mc-avail-day--blocked' : ' mc-avail-day--off';

  const label = `${date}${price ? `, ${price}` : ''}${unavailable ? ', indisponible' : ''}`;

  return (
    <div className={`mc-avail-day${state}`} aria-label={label}>
      {unavailable && price && (
        <ion-icon class="mc-avail-blocked-icon" name="close-circle-outline" />
      )}

      <span className="mc-avail-day-date">{date}</span>

      {price && <span className="mc-avail-price">{price}</span>}
    </div>
  );
}

function CalendarUnvailabilities({ vehiculeId, mois }) {
  const [days, setDays] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [unavailabilities, setUnavailabilities] = useState([]);

  const axios = useAxios();

  // Le mois reçu depuis l'URL est un index compris entre 0 et 11.
  const [currentMonth, setCurrentMonth] = useState(Number(mois));

  // Synchroniser le mois avec le paramètre de route.
  useEffect(() => {
    setCurrentMonth(Number(mois));
  }, [mois]);

  // Obtenir le jour de la semaine :
  // lundi = 0, mardi = 1, ..., dimanche = 6.
  const getDayOfWeek = (date) => {
    const day = date.getDay();
    return day === 0 ? 6 : day - 1;
  };

  // Nombre de jours dans le mois.
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Charger les disponibilités et les indisponibilités du véhicule.
  useEffect(() => {
    if (vehiculeId == null || vehiculeId === '') {
      console.error('vehiculeId est absent de CalendarUnvailabilities');
      return;
    }

    let cancelled = false;

    const fetchCalendarData = async () => {
      try {
        const [availabilityResponse, unavailabilityResponse] = await Promise.all([
          axios.get(`/availabilities/getbyvehiculeid/${vehiculeId}`),
          axios.get(`/unavailabilities/getbyvehiculeid/${vehiculeId}`),
        ]);

        if (cancelled) return;

        const availabilityData = availabilityResponse.data || [];
        const unavailabilityData = unavailabilityResponse.data || [];

        setAvailabilities(availabilityData.filter((item) => item.type == '0'));

        setUnavailabilities(unavailabilityData);
      } catch (error) {
        if (!cancelled) {
          console.error('Erreur lors du chargement du calendrier :', error);
        }
      }
    };

    fetchCalendarData();

    return () => {
      cancelled = true;
    };
  }, [vehiculeId, axios]);

  // Construire les jours du calendrier.
  const createDaysArray = (availabilityList, unavailabilityList, year, month, vehicleId) => {
    const daysInMonth = getDaysInMonth(year, month);
    const days = [];

    // Ajouter les espaces avant le premier jour du mois.
    const firstDayOfMonth = new Date(year, month, 1);
    const startDay = getDayOfWeek(firstDayOfMonth);

    for (let i = 0; i < startDay; i++) {
      days.push({ spacer: true });
    }

    // Ajouter chaque jour du mois.
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDay = new Date(year, month, day);
      const weekDay = String(getDayOfWeek(currentDay));

      const availability = availabilityList.find((item) => String(item.WeekDAY) === weekDay);

      // IMPORTANT :
      // Le véhicule vient directement des props du parent,
      // et non de availability.vehiculeId.
      if (!availability) {
        days.push({
          date: day,
          price: null,
          unavailable: true,
          WeekDAY: weekDay,
          VehiculeId: vehicleId,
        });

        continue;
      }

      const unavailabilityRecurring = unavailabilityList.find(
        (item) => String(item.WeekDAY) === weekDay && String(item.type) === '0'
      );

      const dateOnly = (value) => {
        if (!value) return null;

        const parsedDate = new Date(value);

        return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
      };

      const unavailabilityCustom = unavailabilityList.find((item) => {
        if (String(item.type) !== '1') return false;

        const startDate = dateOnly(item.start_date);
        const endDate = dateOnly(item.end_date);

        return startDate && endDate && currentDay >= startDate && currentDay <= endDate;
      });

      const unavailable = Boolean(unavailabilityRecurring || unavailabilityCustom);

      days.push({
        date: day,
        price: `${availability.Default_price}€`,
        unavailable,
        daysInMonth,
        WeekDAY: availability.WeekDAY,
        VehiculeId: vehicleId,
      });
    }

    return days;
  };

  // Régénérer les jours lorsque les données ou le mois changent.
  useEffect(() => {
    if (
      vehiculeId == null ||
      vehiculeId === '' ||
      !Number.isInteger(currentMonth) ||
      currentMonth < 0 ||
      currentMonth > 11
    ) {
      setDays([]);
      return;
    }

    const year = new Date().getFullYear();

    setDays(createDaysArray(availabilities, unavailabilities, year, currentMonth, vehiculeId));
  }, [availabilities, unavailabilities, currentMonth, vehiculeId]);

  // Calculer le mois précédent et le mois suivant.
  const previousMonth = (currentMonth + 11) % 12;
  const nextMonth = (currentMonth + 1) % 12;

  return (
    <div className="mc-avail-calendar">
      <ToastContainer />

      <div className="mc-avail-header">
        <h2>
          {ALL_MONTHS[currentMonth]} {new Date().getFullYear()}
        </h2>

        <div className="mc-avail-actions">
          <Link
            className="mc-calendar-nav"
            aria-label="Mois précédent"
            to={`/unavailabilities-car/${vehiculeId}/calendar/${previousMonth}`}
          >
            &#10094;
          </Link>

          <Link
            className="mc-calendar-nav"
            aria-label="Mois suivant"
            to={`/unavailabilities-car/${vehiculeId}/calendar/${nextMonth}`}
          >
            &#10095;
          </Link>
        </div>
      </div>

      <div className="mc-avail-weekdays">
        {WEEKDAYS.map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>

      <div className="mc-avail-days">
        {days.map((day, index) => (
          <React.Fragment key={index}>
            {day.spacer ? (
              <div className="mc-avail-day-spacer" />
            ) : (
              <CalendarDayButton date={day.date} price={day.price} unavailable={day.unavailable} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="mc-avail-legend">
        <span>
          <i className="mc-dot mc-dot--ok" />
          Disponible
        </span>

        <span>
          <i className="mc-dot mc-dot--blocked" />
          Indisponible
        </span>

        <span>
          <i className="mc-dot mc-dot--off" />
          Non proposé
        </span>
      </div>
    </div>
  );
}

export default CalendarUnvailabilities;
